/**
 * CRE multi-organisation helpers (Validate `user.organisations` + ProcessWithAI).
 */

const STORAGE_KEY_PREFIX = "cre_selected_organisation";

/** @typedef {{ organisationId: string, name?: string, domain?: string, isDefault?: boolean, totalCredits?: number, remainingCredits?: number }} CreOrganisation */

export function getCreOrganisations(creUser) {
  const list = creUser?.organisations;
  if (!Array.isArray(list)) return [];
  return list.filter((o) => o && o.organisationId);
}

export function getDefaultCreOrganisation(creUser) {
  const orgs = getCreOrganisations(creUser);
  return orgs.find((o) => o.isDefault) ?? orgs[0] ?? null;
}

function storageKey(userId) {
  return `${STORAGE_KEY_PREFIX}:${userId}`;
}

export function readStoredCreOrganisationId(userId) {
  if (!userId) return null;
  try {
    return localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
}

export function writeStoredCreOrganisationId(userId, organisationId) {
  if (!userId || !organisationId) return;
  try {
    localStorage.setItem(storageKey(userId), organisationId);
  } catch {
    /* ignore quota / private mode */
  }
}

export function isCreOrganisationIdValid(creUser, organisationId) {
  if (!organisationId) return false;
  return getCreOrganisations(creUser).some(
    (o) => o.organisationId === organisationId,
  );
}

export function getCreOrganisationById(creUser, organisationId) {
  return getCreOrganisations(creUser).find(
    (o) => o.organisationId === organisationId,
  );
}

export function getCreOrganisationLabel(org) {
  if (!org) return "Organisation";
  return org.name || org.domain || "Organisation";
}

/** `-1` means unlimited (same convention as ReportEditorSection credits). */
export function formatCreOrganisationCredits(org) {
  if (!org) return null;
  if (org.remainingCredits === -1 || org.totalCredits === -1) {
    return "Unlimited credits";
  }
  if (org.remainingCredits != null && org.remainingCredits >= 0) {
    return `${org.remainingCredits.toLocaleString()} credits remaining`;
  }
  return null;
}

/**
 * On Validate: one org → auto-select; multiple → use stored choice only if valid.
 */
export function resolveCreOrganisationId(creUser) {
  const orgs = getCreOrganisations(creUser);
  if (!orgs.length) return null;
  if (orgs.length === 1) return orgs[0].organisationId;

  const userId = creUser?.userId;
  const stored = userId ? readStoredCreOrganisationId(userId) : null;
  if (stored && isCreOrganisationIdValid(creUser, stored)) {
    return stored;
  }
  return null;
}

export function needsCreOrganisationSelection(creUser, selectedOrganisationId) {
  const orgs = getCreOrganisations(creUser);
  if (orgs.length <= 1) return false;
  return !isCreOrganisationIdValid(creUser, selectedOrganisationId);
}

export function appendCreOrganisationToAiPayload(payload, organisationId) {
  if (!organisationId) return payload;
  return { ...payload, OrganisationId: organisationId };
}

/** Selected CRE org for reporting APIs (picker value, else single-org / stored). */
export function getSelectedCreOrganisationId(
  creUser,
  creSelectedOrganisationId,
) {
  return creSelectedOrganisationId || resolveCreOrganisationId(creUser) || null;
}
